// components/events/eventcomponents/EventCapacityDisplay.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FaUsers, FaFire, FaTag } from 'react-icons/fa';
import axios from '@/utils/axiosConfig';

const EventCapacityDisplay = ({ 
  eventId, 
  isPaid = false, 
  showInTicketSection = false, 
  maxPerRegistration,
  context = 'viewing', // 'viewing' | 'purchasing'
  stack = false, // when true, put "Max" on its own line under the capacity (for narrow columns)
  onCapacityUpdate, // Callback when capacity changes
  refreshInterval // Custom refresh interval (ms)
}) => {
  // Local state for capacity data
  const [capacity, setCapacity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Still listen to registration status for refresh trigger
  const registrationStatus = useSelector(state => state.freeEvents.registrationStatus);

  // Fetch capacity function
  const fetchCapacity = useCallback(async () => {
    if (!eventId) {
      console.log(`❌ EventCapacityDisplay: No eventId provided, skipping fetch`);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Always use v2 endpoint with context
      const endpoint = `${process.env.NEXT_PUBLIC_API_BASE_URL}/events/${eventId}/capacity/v2?context=${context}`;
      
      console.log(`📊 EventCapacityDisplay: Fetching capacity from ${endpoint}`);
      
      const response = await axios.get(endpoint, { withCredentials: true });
      
      if (response.data.success) {
        const newCapacity = response.data.capacity;
        setCapacity(newCapacity);
        console.log(`✅ EventCapacityDisplay: Capacity fetched for ${eventId}:`, newCapacity);
        
        // Notify parent component if callback provided
        if (onCapacityUpdate) {
          onCapacityUpdate(newCapacity);
        }
      } else {
        throw new Error(response.data.message || 'Failed to fetch capacity');
      }
    } catch (err) {
      console.error(`❌ EventCapacityDisplay: Error fetching capacity:`, err);
      setError(err.response?.data?.message || err.message || 'Unable to load capacity information');
    } finally {
      setIsLoading(false);
    }
  }, [eventId, context, onCapacityUpdate]);

  // Initial fetch
  useEffect(() => {
    fetchCapacity();
  }, [fetchCapacity]);

  // Set up polling based on context and event type
  useEffect(() => {
    if (!eventId) return;
    
    // Determine polling interval
    let interval;
    if (refreshInterval) {
      // Use custom interval if provided
      interval = refreshInterval;
    } else if (context === 'purchasing') {
      // More frequent updates when purchasing
      interval = 10000; // 10 seconds
    } else if (!isPaid) {
      // Free events in viewing mode
      interval = 30000; // 30 seconds
    } else {
      // Paid events in viewing mode
      interval = 60000; // 60 seconds
    }
    
    const timer = setInterval(() => {
      // Don't poll while the tab is backgrounded — a timer that fires on a
      // suspended/locked device hits a dead network and errors out.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      console.log(`🔄 EventCapacityDisplay: Polling capacity for ${eventId} (${context} mode)`);
      fetchCapacity();
    }, interval);

    return () => clearInterval(timer);
  }, [eventId, context, isPaid, refreshInterval, fetchCapacity]);

  // Refetch when the page returns to the foreground (e.g. phone unlocked) so a
  // stale value refreshes promptly without waiting for the next poll tick.
  useEffect(() => {
    if (!eventId) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchCapacity();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [eventId, fetchCapacity]);

  // Refresh capacity when registration/unregistration succeeds
  useEffect(() => {
    if (registrationStatus === 'succeeded' && eventId) {
      console.log(`🔄 Registration succeeded, refreshing capacity for ${eventId}`);
      fetchCapacity();
    }
  }, [registrationStatus, eventId, fetchCapacity]);

  // ✅ Loading state — only on first load. A background refresh keeps the
  // last-known value on screen instead of flashing the skeleton.
  if (isLoading && !capacity) {
    return (
      <div className="animate-pulse grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
      </div>
    );
  }

  // ✅ Error state — only when we have nothing to show. A failed *refresh*
  // (e.g. a network blip when the phone wakes from lock) keeps the last-known
  // capacity on screen and retries quietly, instead of blanking to an error.
  if (error && !capacity) {
    return (
      <div className="text-sm text-red-600">
        Unable to load capacity information
      </div>
    );
  }

  // ✅ No data state
  if (!capacity) {
    return null;
  }

  // ✅ Context-specific rendering for purchasing mode
  if (context === 'purchasing' && capacity) {
    const { 
      available, 
      userReservation, 
      canBook, 
      isUnlimited, 
      status,
      activeReservations 
    } = capacity;
    
    return (
      <div className="space-y-2">
        <div className="flex items-center text-sm text-slate-600">
          <FaUsers className="mr-2" />
          <span>
            {isUnlimited ? (
              'Unlimited capacity'
            ) : status === 'sold-out' ? (
              <span className="text-red-600 font-semibold">Event sold out</span>
            ) : available === 0 ? (
              <span className="text-orange-600">No spots currently available</span>
            ) : available < 5 ? (
              <span className="text-orange-600">Only {available} spots remaining!</span>
            ) : (
              `${available} spots available`
            )}
          </span>
        </div>
        
        {userReservation > 0 && (
          <div className="text-xs text-blue-600">
            You have {userReservation} tickets reserved
          </div>
        )}
        
        {activeReservations > 0 && status === 'limited' && (
          <div className="text-xs text-slate-500">
            {activeReservations} other users are currently booking
          </div>
        )}
      </div>
    );
  }
  
  // ✅ For paid events in ticket section (legacy support)
  if (isPaid && showInTicketSection) {
    const { displayText } = capacity;
    return (
      <div className="flex items-center text-sm text-slate-600 mb-4">
        <FaUsers className="mr-2" />
        <span>{displayText || 'Loading capacity...'}</span>
      </div>
    );
  }

  // ✅ Full display for viewing context (free and paid events)
  const {
    total: totalCapacity,
    sold: registeredCount,
    remaining: remainingCapacity,
    isUnlimited,
    status,
    confirmedAllocations,
    displayText,
    lastUpdated
  } = capacity;

  // Use confirmedAllocations for accurate sold out display
  const isFull = totalCapacity > 0 && (confirmedAllocations || registeredCount) >= totalCapacity;

  // Scarcity "hype": when only a few spots remain (and not sold out), the capacity line turns
  // urgent (fire icon + bold orange) so low availability reads as "hurry" instead of a calm
  // grey line. Above the threshold it stays the normal calm style.
  const spotsRemaining = typeof remainingCapacity === 'number' ? remainingCapacity : null;
  const isScarce = !isUnlimited && !isFull && spotsRemaining !== null && spotsRemaining > 0 && spotsRemaining <= 10;

  // Create consistent display text for free events if not provided
  const consistentDisplayText = displayText || (!isPaid && !isUnlimited
    ? `${remainingCapacity} of ${totalCapacity} spots left`
    : isUnlimited ? 'Unlimited capacity' : displayText);

  // Availability value for the two-row key/value layout: "297 / 300 left" (or "Unlimited"),
  // falling back to the display text if the raw numbers aren't clean.
  const availabilityValue = isUnlimited
    ? 'Unlimited'
    : (typeof remainingCapacity === 'number' && totalCapacity > 0)
      ? `${remainingCapacity} / ${totalCapacity}`
      : consistentDisplayText;

  // Sold-out / fully-booked: a single clean line. No point repeating it as a summary AND a
  // status, and Max-per-order is irrelevant once nothing is available.
  if (isFull) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 text-xs whitespace-nowrap rounded-full bg-red-50 border border-red-200 text-red-700">
        <FaUsers className="shrink-0" size={12} />
        {isPaid ? 'Sold Out' : 'Fully Booked'}
      </div>
    );
  }

  // Two labelled key/value rows: Availability, then Order Limit. Icon + label on the left,
  // value pushed to the right (ml-auto). Availability turns urgent (fire + amber) when scarce.
  return (
    <div className="flex flex-col gap-2 text-sm">
      {/* Availability */}
      {availabilityValue && (
        <div className="flex items-center gap-2">
          {/* People icon is the constant Availability label; the fire is an urgency marker that
              rides next to the number only when scarce. */}
          <FaUsers className="shrink-0 text-slate-500" size={13} />
          <span className="text-slate-500">Availability</span>
          <span className={`ml-auto inline-flex items-center gap-1 text-right font-semibold whitespace-nowrap ${isScarce ? 'text-amber-700' : 'text-slate-700'}`}>
            {isScarce && <FaFire className="shrink-0 text-amber-500 animate-pulse" size={13} />}
            {availabilityValue}
          </span>
        </div>
      )}

      {/* Order Limit */}
      {typeof maxPerRegistration === 'number' && (
        <div className="flex items-center gap-2">
          <FaTag className="shrink-0 text-slate-500" size={12} />
          <span className="text-slate-500">Order Limit</span>
          <span className="ml-auto text-right font-semibold text-slate-700 whitespace-nowrap">
            Max {maxPerRegistration}
          </span>
        </div>
      )}
    </div>
  );
};

export default EventCapacityDisplay;